import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { FinalCTA } from "@/pages/Home";
import { useSiteContent } from "@/lib/content";

const PORTRAIT = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1400&auto=format&fit=crop";

const About = () => {
  const content = useSiteContent();
  const tx = (key, fallback) => content?.texts?.[key] || fallback;

  const phases = [1, 2, 3].map((n) => ({
    title: tx(`about_phase${n}_t`, ["Fotografia", "Produzione visiva", "AI pragmatica"][n - 1]),
    text: tx(`about_phase${n}_x`, ""),
  }));

  return (
    <main data-testid="about-page" className="bg-night pt-32">
      <div className="px-6 sm:px-12 lg:px-20">
        <Reveal>
          <Seo
            title="Chi sono — Nicola Verardo, Visual Production"
            description="Da ventisei anni costruisco valore percepito: da fotografo di still life a boutique di visual production. Pochi clienti alla volta, personalmente."
            path="/about"
          />
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-fog">{tx("about_label", "About")}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-bone max-w-3xl">
            {tx("about_title", "Da ventisei anni costruisco valore percepito.")}
          </h1>
        </Reveal>
      </div>

      <section className="px-6 sm:px-12 lg:px-20 mt-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-5">
          <img
            src={content?.about_portrait || PORTRAIT}
            alt="Nicola Verardo, ritratto in bianco e nero"
            loading="lazy"
            className="w-full aspect-[4/5] object-cover grayscale"
          />
          <p className="mt-3 text-[10px] font-mono tracking-[0.25em] text-fog">{tx("about_caption", "NICOLA VERARDO — STUDIO, 2026")}</p>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-7">
          <p className="text-lg sm:text-xl text-bone leading-relaxed font-light">
            {tx("about_p1", "Ho iniziato come fotografo di still life quando il digitale era una promessa. Ho lavorato tra Italia ed estero per brand che vendono attraverso le immagini: arredo, cosmetica, orologeria, ospitalità.")}
          </p>
          <p className="mt-6 text-base text-fog leading-relaxed">
            {tx("about_p2", "A un certo punto ho capito che ai miei clienti non serviva un fotografo in più: serviva qualcuno che progettasse il loro intero ecosistema visivo. Così lo studio è diventato una boutique di visual production: fotografia, motion e AI coordinati sotto un'unica direzione.")}
          </p>
          <p className="mt-6 text-base text-fog leading-relaxed">
            {tx("about_p3", "Lavoro con pochi clienti alla volta, personalmente. Nessuna gerarchia d'agenzia, nessun account di mezzo: parli con chi produce.")}
          </p>
          <div className="mt-12 grid sm:grid-cols-3 gap-8">
            {phases.map((phase, i) => (
              <div key={i} data-testid={`about-phase-${i}`} className="border-t border-line pt-6">
                <p className="text-xs font-mono tracking-[0.25em] text-gold uppercase">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 text-lg font-medium text-bone">{phase.title}</h3>
                <p className="mt-3 text-sm text-fog leading-relaxed">{phase.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mt-24 bg-bone text-night">
        <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28 max-w-4xl">
          <Reveal>
            <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-snug">
              {tx("about_quote", "«Da portatore d'acqua a costruttore di acquedotti.»")}
            </blockquote>
            <p className="mt-8 text-base text-night/60 leading-relaxed max-w-2xl">
              {tx("about_quote_text", "Per anni ho consegnato immagini una alla volta, come chi porta acqua a secchi. Oggi progetto sistemi: visual che continuano a lavorare per il brand molto dopo la consegna. È la differenza tra produrre fotografie e costruire infrastrutture di valore.")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28">
        <Reveal className="flex flex-col sm:flex-row sm:items-center justify-between gap-8">
          <h2 className="text-2xl sm:text-3xl font-normal tracking-tight text-bone max-w-xl">
            {tx("about_cta_title", "Se cerchi un partner visivo, non un fornitore di foto, sei nel posto giusto.")}
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone transition-colors duration-300 w-fit shrink-0"
          >
            {tx("about_cta_button", "PARLIAMONE")} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </section>

      <FinalCTA />
    </main>
  );
};

export default About;
